<script>
import { GlLoadingIcon, GlAlert, GlResizeObserverDirective } from '@gitlab/ui';
import { throttle } from 'lodash-es';
import Z from 'vendor/zuora';
import {
  zuoraRenderCallback,
  zuoraErrorMessageCallback,
  subscribeToZuoraErrorEvent,
  subscribeToZuoraSuccessEvent,
  unsubscribeFromZuoraErrorEvent,
  unsubscribeFromZuoraSuccessEvent,
} from '~/javascripts/lib/zuora_utils';
import { fetchZuoraParams } from '~/javascripts/api';

export default {
  name: 'ZuoraNewCreditCardForm',
  components: {
    GlLoadingIcon,
    GlAlert,
  },
  directives: {
    GlResizeObserver: GlResizeObserverDirective,
  },
  props: {
    flow: {
      type: String,
      required: false,
      default: 'cc',
    },
    inlineSubmit: {
      type: Boolean,
      required: false,
      default: false,
    },
    invoiceNumber: {
      type: String,
      required: false,
      default: null,
    },
    paymentFormClasses: {
      type: [Array, Object, String],
      required: false,
      default: 'gl-grow gl-min-w-31 gl-bg-blue-50',
    },
  },
  emits: ['success', 'renderCallback'],
  data() {
    return {
      isLoading: false,
      errorMessage: null,
    };
  },
  async mounted() {
    this.isLoading = true;
    subscribeToZuoraErrorEvent(this.renderZuoraIframe);
    subscribeToZuoraSuccessEvent(this.handleSuccess);
    await this.renderZuoraIframe();
  },
  beforeDestroy() {
    unsubscribeFromZuoraErrorEvent(this.renderZuoraIframe);
    unsubscribeFromZuoraSuccessEvent(this.handleSuccess);
  },
  methods: {
    handleSuccess() {
      this.$emit('success');
    },
    /**
     * We let the width of the iframe grow/shrink with its container using flexbox,
     * but we need to adjust the height as well and we do that by listening to a resize
     * event on the container and use the iframe's message bus to ask it to update its size
     * by sending the `resize` to the iframe.
     */
    handleResize: throttle(() => {
      const iframe = document.querySelector('#z_hppm_iframe');
      if (iframe) {
        Z.post('z_hppm_iframe', 'resize');
      }
    }, 250),
    renderCallBack(response) {
      zuoraRenderCallback(response);
      this.$emit('renderCallback', response);
    },
    async renderZuoraIframe() {
      this.errorMessage = null;
      try {
        const formParams = await fetchZuoraParams(this.flow, {
          invoiceNumber: this.invoiceNumber,
          inlineSubmit: this.inlineSubmit,
        });

        // Register callback before we call render
        Z.runAfterRender(() => {
          this.isLoading = false;
        });
        Z.renderWithErrorHandler(formParams, {}, this.renderCallBack, zuoraErrorMessageCallback);
      } catch {
        this.errorMessage = 'Could not load form, please refresh your browser';
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div
    v-gl-resize-observer="handleResize"
    class="zuora-container gl-flex gl-flex-wrap gl-items-baseline gl-justify-center"
  >
    <gl-alert v-if="errorMessage">{{ errorMessage }}</gl-alert>
    <gl-loading-icon v-else-if="isLoading" class="gl-m-auto" size="lg" />
    <div v-show="!isLoading && !errorMessage" id="zuora_payment" :class="paymentFormClasses"></div>
  </div>
</template>
